import { useRef, useState } from 'react';
import styled from '@emotion/styled';
import type { MicrositeResponsiveImage } from '~/model';
import MicrositeResponsiveImg from '~/components/MicrositeResponsiveImg';
import { LoadPriority } from '~/hooks/useLoadPriority';
import { css_background } from '~/emotion/theme';

/* ── styled components ── */

const Wrapper = styled.div`
	background: ${css_background()};
	color: ${(props) => props.theme.color.black.text.strong};
	padding: 0.75rem;
	max-width: 1536px;
	margin-left: auto;
	margin-right: auto;
`;

const Header = styled.div`
	@media (min-width: 768px) {
		height: 100px;
		display: flex;
		flex-direction: column;
		justify-content: center;
	}
`;

const Title = styled.div`
	text-transform: uppercase;
	font-weight: 500;
	font-size: 1.5rem;
	line-height: 2rem;
	letter-spacing: 0.3em;
	text-align: center;
	@media (min-width: 768px) {
		font-size: 2.25rem;
		line-height: 2.5rem;
		padding-bottom: 0.5rem;
	}
`;

const Subtitle = styled.div`
	letter-spacing: 0.1em;
	text-align: center;
`;

const Grid = styled.div<{ showAll: boolean }>`
	@media (min-width: 768px) {
		display: grid;
		grid-template-columns: repeat(10, 1fr);
		grid-auto-rows: minmax(175px, calc(50vh - 50px));
	}

	& > a {
		padding: 0.75rem;
	}

	/* hide items beyond 10 unless expanded */
	${({ showAll }) =>
		!showAll &&
		`
		& > a:nth-of-type(n + 11) {
			display: none;
		}
	`}

	/* 5n+1: large left tile */
	& > a:nth-of-type(5n + 1) {
		grid-column: span 6;
		grid-row: span 2;
	}
	& > a:nth-of-type(5n + 1):last-child {
		grid-column: span 10;
	}

	/* 5n+2: small right-top */
	& > a:nth-of-type(5n + 2) {
		grid-column: span 4;
	}
	& > a:nth-of-type(5n + 2):last-child {
		grid-row: span 2;
	}

	/* 5n+3: small right-bottom */
	& > a:nth-of-type(5n + 3) {
		grid-column: span 4;
	}

	/* 5n+4: large right tile */
	& > a:nth-of-type(5n + 4) {
		grid-column: span 4;
		grid-row: span 2;
	}
	& > a:nth-of-type(5n + 4):last-child {
		grid-column: span 10;
	}

	/* 5n+5: large left tile (mirrored) */
	& > a:nth-of-type(5n + 5) {
		grid-column: span 6;
		grid-row: span 2;
	}
`;

const ShowAllBar = styled.div<{ hidden: boolean }>`
	display: ${({ hidden }) => (hidden ? 'none' : 'flex')};
	position: relative;
	width: 100%;
	justify-content: center;
	align-items: center;
	padding-top: 1rem;
	padding-bottom: 1rem;
	box-shadow: 0 -25px 16px 13px rgba(0, 0, 0, 0.88);
`;

const ShowAllButton = styled.button`
	padding: 0.5rem 1rem;
	border: 2px solid white;
	background: transparent;
	color: inherit;
	cursor: pointer;
`;

/* ── component ── */

export default function GalleryBlockEta({ photos }: { photos: MicrositeResponsiveImage[]; navigation?: boolean }) {
	const [showAll, setShowAll] = useState(false);
	const ref = useRef<HTMLButtonElement>(null);

	if (photos.length === 0) return null;

	return (
		<Wrapper>
			<Header>
				<Title>Photo Gallery</Title>
				<Subtitle>Tap any photo for a full screen slide show.</Subtitle>
			</Header>
			<Grid showAll={showAll}>
				{photos.map((photo, index) => (
					<a key={index} href="#gallery" data-index={index}>
						<MicrositeResponsiveImg image={photo} priority={LoadPriority.FIRST_PAINT} />
					</a>
				))}
			</Grid>
			<ShowAllBar hidden={showAll}>
				<ShowAllButton ref={ref} onClick={() => setShowAll(true)}>
					View all <b>{photos.length}</b> photos
				</ShowAllButton>
			</ShowAllBar>
		</Wrapper>
	);
}
